import { useEffect, useRef } from 'react';
import { useFeatureFlags } from 'components/FeatureFlags';
import logger from 'util/logger';
import useProjectSections from 'components/ScriptStudio/Sections/hooks/useProjectSections';
import { AppState, store } from 'store';
import { useAppDispatch } from 'store/hooks';
import {
  clearOutdatedDraft,
  setAvatarDraft,
  setAvatarVariantDraft,
} from 'store/ScriptStudioBuffersSlice';
import { useUpdateSectionVoicesMutation } from 'components/ScriptStudio/Sections/operation.generated';
import { SectionFragment } from 'components/fragments.generated';
import { UpdateSectionVoicesInputs } from '../../../../../server/graphql/__generated.types';

/**
 * Finds sections whose voice should be persisted, keyed by
 * `avatarId:variantId:version`, and local voice drafts to replace with the
 * saved voice because it is newer. A saved voice changed after the draft was
 * last edited is someone else's choice, so it wins over the stale draft.
 */
export function getVoiceReconciliation(
  sections: SectionFragment[],
  sectionDraftsById: AppState['scriptStudioBuffers']['byId'],
  // the model is only saved and compared for `section-editor-sync`
  compareModel = false
) {
  const mismatches = new Map<string, string[]>();
  const staleDrafts: Array<{
    id: string;
    avatarId: number;
    variantId: number;
    ttsVersion?: string;
    takeCreatedAt?: string;
  }> = [];

  sections.forEach(section => {
    const currentDefaultAvatarId = section.defaultAvatarId;
    const currentDefaultAvatarVariantId = section.defaultAvatarVariantId;
    const currentDefaultTtsVersion = compareModel
      ? section.defaultTtsVersion ?? ''
      : '';

    const currentTakeAvatarId = section.currentTake?.avatarId;
    const currentTakeAvatarVariantId = section.currentTake?.avatarVariantId;
    const currentTakeTtsVersion = compareModel
      ? section.currentTake?.ttsVersion ?? ''
      : '';

    const sectionDraft = sectionDraftsById[section.id];
    const hasSectionDraft =
      sectionDraft &&
      typeof sectionDraft.avatarDraft === 'number' &&
      typeof sectionDraft.avatarVariantDraft === 'number';
    const draftTtsVersion = compareModel
      ? sectionDraft?.avatarVariantVersionDraft ?? ''
      : '';
    const hasSavedVoice =
      typeof currentDefaultAvatarId === 'number' &&
      typeof currentDefaultAvatarVariantId === 'number';

    /**
     * Could represent a draft that is loaded into project but isn't generated yet
     */
    if (
      hasSectionDraft &&
      !(
        currentDefaultAvatarId === sectionDraft.avatarDraft &&
        currentDefaultAvatarVariantId === sectionDraft.avatarVariantDraft &&
        currentDefaultTtsVersion === draftTtsVersion
      )
    ) {
      if (
        hasSavedVoice &&
        Number(section.updatedAt) > sectionDraft.lastEditedAt
      ) {
        staleDrafts.push({
          id: section.id,
          avatarId: currentDefaultAvatarId,
          variantId: currentDefaultAvatarVariantId,
          ttsVersion: currentDefaultTtsVersion || undefined,
          takeCreatedAt: section.currentTake?.createdAt,
        });
        return;
      }
      const key = `${sectionDraft.avatarDraft!}:${sectionDraft.avatarVariantDraft!}:${draftTtsVersion}`;
      mismatches.set(key, [...(mismatches.get(key) ?? []), section.id]);
      return;
    }

    const currentTakeIdsExist =
      typeof currentTakeAvatarId === 'number' &&
      typeof currentTakeAvatarVariantId === 'number';
    /**
     * Backfills a section that has no saved voice from its most recent take.
     * A saved default is someone's choice, even if it hasn't been generated
     * yet, so opening the project must never overwrite it.
     */
    if (!hasSectionDraft && !hasSavedVoice && currentTakeIdsExist) {
      const key = `${currentTakeAvatarId}:${currentTakeAvatarVariantId}:${currentTakeTtsVersion}`;
      mismatches.set(key, [...(mismatches.get(key) ?? []), section.id]);
    }
  });

  return { mismatches, staleDrafts };
}

export default function useInitializeSectionVoices({
  projectId,
}: {
  projectId: string;
}) {
  const dispatch = useAppDispatch();
  const { isFeatureEnabled } = useFeatureFlags();
  // note: this voice persistence is relevant for realtime updates
  // once either of these feature launch we can reduce this
  const persistVoice =
    isFeatureEnabled('guided-project-creation') ||
    isFeatureEnabled('section-editor-sync');
  const [updateSectionVoices] = useUpdateSectionVoicesMutation();
  const { sections, loading } = useProjectSections({
    projectId,
    skip: !persistVoice,
  });
  const syncModel = isFeatureEnabled('section-editor-sync');
  const reconciled = useRef(false);

  useEffect(
    () => {
      // Reconcile once, after the sections have loaded
      if (!persistVoice || reconciled.current) return;
      if (loading || sections.length === 0) return;
      reconciled.current = true;

      const { mismatches: voiceMismatches, staleDrafts } =
        getVoiceReconciliation(
          sections,
          store.getState().scriptStudioBuffers.byId,
          syncModel
        );
      if (staleDrafts.length > 0) {
        logger.info(`Replacing stale voice drafts ${staleDrafts.length}`);
        /**
         * Clearing the drafts would show the current take's voice, which comes
         * before the saved voice, so write the saved voice into them instead.
         * A buffer a newer take outdated is dropped first: the draft write
         * would refresh its timestamp and bring back its stale text.
         */
        staleDrafts.forEach(draft => {
          if (draft.takeCreatedAt) {
            dispatch(
              clearOutdatedDraft({
                id: draft.id,
                takeCreatedAt: draft.takeCreatedAt,
              })
            );
          }
          dispatch(
            setAvatarDraft({ id: draft.id, projectId, avatar: draft.avatarId })
          );
          dispatch(
            setAvatarVariantDraft({
              id: draft.id,
              projectId,
              avatarVariant: draft.variantId,
              avatarVariantVersion: draft.ttsVersion,
            })
          );
        });
      }
      if (voiceMismatches.size > 0) {
        logger.info(`Detected section mismatch ${voiceMismatches.size}`);

        const sectionsInputs: UpdateSectionVoicesInputs['sections'] =
          Array.from(voiceMismatches.entries()).flatMap(entry => {
            const avatarIds = entry[0].split(':');
            const pendingAvatarId = avatarIds.at(0);
            const pendingVariantId = avatarIds.at(1);
            const pendingTtsVersion = avatarIds.at(2);
            const avatarId = pendingAvatarId
              ? parseInt(pendingAvatarId, 10)
              : undefined;
            const variantId = pendingVariantId
              ? parseInt(pendingVariantId, 10)
              : undefined;

            if (typeof avatarId === 'number' && typeof variantId === 'number') {
              return [
                {
                  sectionIds: entry[1],
                  defaultAvatarId: avatarId,
                  defaultAvatarVariantId: variantId,
                  defaultTtsVersion: syncModel
                    ? pendingTtsVersion || null
                    : undefined,
                },
              ];
            }
            return [];
          });

        if (sectionsInputs.length === 0) {
          logger.warn(`Found section mismatch, failed to form inputs`);
          return;
        }

        logger.info(`Updating section voices`);
        updateSectionVoices({
          variables: {
            inputs: { sections: sectionsInputs },
          },
        })
          .then(({ data }) => {
            if (!data?.updateSectionVoices.success) {
              logger.info(`Failed to update mismatched section voices`, {
                payload: data?.updateSectionVoices,
              });
              return;
            }
            logger.info(`Successfully updated mismatched section voices`);
          })
          .catch(err => {
            logger.error(`Failed to update mismatched section voices`, err);
          });
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loading, sections.length, persistVoice]
  );
}
